import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Globe, 
  Save,
  Loader2,
  Type,
  BarChart3,
  Image,
  Phone,
  Clock,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const AdminHomePage = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const res = await api.get('/website/content/home').catch(() => ({ data: null }));
      setContent(res.data || {
        hero: {
          title: 'Boostons la',
          highlight: 'visibilité',
          subtitle: 'de votre entreprise',
          description: 'Agence web spécialisée dans la création de sites internet, le référencement et la communication digitale.',
        },
        stats: {
          clients: 150,
          projects: 500,
          rating: 4.9,
          reviews: 150
        },
        services: ['Sites Web', 'SEO', 'Vidéo', 'Design', 'Marketing'],
        cta: {
          primary: 'Demander un devis',
          secondary: 'Voir nos réalisations'
        },
        schedule: {
          text: 'Lun-Ven 9h-18h',
          status: 'Ouvert'
        }
      });
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/website/content/home', content);
      toast.success('Contenu sauvegardé');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a365d]">Page d'accueil</h1>
          <p className="text-[#64748b]">Modifiez le contenu de la page d'accueil du site vitrine</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      <Tabs defaultValue="hero" className="space-y-6">
        <TabsList>
          <TabsTrigger value="hero"><Type className="w-4 h-4 mr-2" />Hero</TabsTrigger>
          <TabsTrigger value="stats"><BarChart3 className="w-4 h-4 mr-2" />Statistiques</TabsTrigger>
          <TabsTrigger value="schedule"><Clock className="w-4 h-4 mr-2" />Horaires</TabsTrigger>
        </TabsList>

        <TabsContent value="hero" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Section Hero</CardTitle>
              <CardDescription>Le titre principal affiché en haut de la page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Titre (ligne 1)</Label>
                  <Input 
                    value={content?.hero?.title || ''}
                    onChange={(e) => setContent({...content, hero: {...content.hero, title: e.target.value}})}
                    placeholder="Boostons la"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mot mis en avant</Label>
                  <Input 
                    value={content?.hero?.highlight || ''}
                    onChange={(e) => setContent({...content, hero: {...content.hero, highlight: e.target.value}})}
                    placeholder="visibilité"
                    className="border-amber-300 focus:border-amber-500"
                  />
                  <p className="text-xs text-amber-600">Ce mot sera coloré avec la couleur d'accent</p>
                </div>
                <div className="space-y-2">
                  <Label>Titre (ligne 2)</Label>
                  <Input 
                    value={content?.hero?.subtitle || ''}
                    onChange={(e) => setContent({...content, hero: {...content.hero, subtitle: e.target.value}})}
                    placeholder="de votre entreprise"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea 
                  value={content?.hero?.description || ''}
                  onChange={(e) => setContent({...content, hero: {...content.hero, description: e.target.value}})}
                  placeholder="Agence web spécialisée dans..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Services affichés</CardTitle>
              <CardDescription>Les tags de services visibles sous le titre (séparés par des virgules)</CardDescription>
            </CardHeader>
            <CardContent>
              <Input 
                value={(content?.services || []).join(', ')}
                onChange={(e) => setContent({...content, services: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
                placeholder="Sites Web, SEO, Vidéo, Design, Marketing"
              />
              <div className="flex flex-wrap gap-2 mt-4">
                {(content?.services || []).map((service, idx) => (
                  <span key={idx} className="px-3 py-1 rounded-full text-sm bg-slate-100 text-slate-700">
                    {service}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card className="bg-[#1a365d] text-white">
            <CardHeader>
              <CardTitle className="text-white">Aperçu</CardTitle>
            </CardHeader>
            <CardContent>
              <h2 className="text-3xl font-bold mb-2">
                {content?.hero?.title}{' '}
                <span className="text-[#00d4aa]">{content?.hero?.highlight}</span>
              </h2>
              <h2 className="text-3xl font-bold mb-4">{content?.hero?.subtitle}</h2>
              <p className="text-white/70 mb-4">{content?.hero?.description}</p>
              <div className="flex flex-wrap gap-2">
                {(content?.services || []).map((service, idx) => (
                  <span key={idx} className="px-3 py-1 rounded-full text-sm border border-white/20 bg-white/10">
                    {service}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stats" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-600" />
                Chiffres clés
              </CardTitle>
              <CardDescription>Ces statistiques sont affichées sur la page d'accueil et la page de login</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="space-y-2">
                  <Label>Nombre de clients</Label>
                  <Input 
                    type="number"
                    value={content?.stats?.clients || 0}
                    onChange={(e) => setContent({...content, stats: {...content.stats, clients: parseInt(e.target.value) || 0}})}
                  />
                  <p className="text-xs text-slate-500">Affiché comme "150+"</p>
                </div>
                <div className="space-y-2">
                  <Label>Nombre de projets</Label>
                  <Input 
                    type="number"
                    value={content?.stats?.projects || 0}
                    onChange={(e) => setContent({...content, stats: {...content.stats, projects: parseInt(e.target.value) || 0}})}
                  />
                  <p className="text-xs text-slate-500">Affiché comme "500+"</p>
                </div>
                <div className="space-y-2">
                  <Label>Note Google</Label>
                  <Input 
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={content?.stats?.rating || 4.9}
                    onChange={(e) => setContent({...content, stats: {...content.stats, rating: parseFloat(e.target.value) || 0}})}
                  />
                  <p className="text-xs text-slate-500">Sur 5 étoiles</p>
                </div>
                <div className="space-y-2">
                  <Label>Nombre d'avis</Label>
                  <Input 
                    type="number"
                    value={content?.stats?.reviews || 0}
                    onChange={(e) => setContent({...content, stats: {...content.stats, reviews: parseInt(e.target.value) || 0}})}
                  />
                </div>
              </div>

              {/* Stats Preview */}
              <div className="mt-6 p-4 rounded-lg bg-slate-50 grid grid-cols-3 gap-4">
                <div>
                  <div className="text-2xl font-bold text-[#1a365d]">{content?.stats?.clients}+</div>
                  <div className="text-slate-500 text-sm">Clients</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#1a365d]">{content?.stats?.projects}+</div>
                  <div className="text-slate-500 text-sm">Projets</div>
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <span className="text-2xl font-bold text-[#1a365d]">{content?.stats?.rating}</span>
                  </div>
                  <div className="text-slate-500 text-sm">Google ({content?.stats?.reviews} avis)</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="schedule" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                Horaires d'ouverture
              </CardTitle>
              <CardDescription>Affichés dans le badge en haut de page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <select 
                    className="w-full p-2 border rounded-lg"
                    value={content?.schedule?.status || 'Ouvert'}
                    onChange={(e) => setContent({...content, schedule: {...content.schedule, status: e.target.value}})}
                  >
                    <option value="Ouvert">Ouvert</option>
                    <option value="Fermé">Fermé</option>
                    <option value="Bientôt fermé">Bientôt fermé</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Horaires texte</Label>
                  <Input 
                    value={content?.schedule?.text || ''}
                    onChange={(e) => setContent({...content, schedule: {...content.schedule, text: e.target.value}})}
                    placeholder="Lun-Ven 9h-18h"
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="mt-4 p-4 rounded-lg bg-[#1a365d] inline-block">
                <span className="px-3 py-1 rounded-full text-sm bg-white/20 text-white flex items-center gap-2">
                  <Clock className="w-3 h-3" />
                  {content?.schedule?.status} · {content?.schedule?.text}
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminHomePage;
